import React, { useEffect, useState } from 'react';
import { useDialog } from '../../context/DialogContext';

/**
 * USE YOUR OWN DOMAIN — a page sending from hello@theirbusiness.com. Portal can't write their DNS,
 * so this shows the three records to add (copy each, or email them to whoever runs the domain);
 * the server checks every few minutes and switches the page's email over once Brevo sees them.
 * Until then — and if the records ever vanish — email goes out from `fallbackFrom`.
 */
const STATUS = {
  pending: ['WAITING FOR DNS', '#FEF3C7', '#000'],
  verified: ['SENDING FROM IT', '#22C55E', '#000'],
  broken: ['RECORDS MISSING', '#FEE2E2', '#B91C1C'],
};
const PILL = 'inline-flex items-center justify-center rounded-full font-mono font-extrabold uppercase transition-colors duration-200 focus:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4px_#000] disabled:opacity-60';

export default function OwnDomainPanel({ client, domain, fallbackFrom, readOnly, onChange }) {
  const { showConfirm } = useDialog();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [opts, setOpts] = useState(null);
  const [cfToken, setCfToken] = useState('');
  const [cfDone, setCfDone] = useState(null);

  // How to get the records there: the detected host, its DNS page, one-click Domain Connect when
  // the host supports it, a Cloudflare token when the zone is on Cloudflare.
  const domainKey = domain ? `${domain.domain}|${domain.status}` : null;
  useEffect(() => {
    let live = true;
    if (!domain || domain.status === 'verified' || !client.domainOptions) { setOpts(null); return undefined; }
    const back = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
    client.domainOptions(back).then((r) => { if (live && r?.success) setOpts(r.data); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, domainKey]);

  const run = async (what, fn) => {
    setBusy(what); setError(null);
    const r = await fn();
    setBusy(null);
    if (!r?.success) { setError(r?.message || r?.error || 'Try again.'); return null; }
    return r;
  };
  const connect = async () => {
    const r = await run('connect', () => client.connectDomain(address));
    if (r) { onChange(r.data); setOpen(false); setAddress(''); }
  };
  const viaCloudflare = async () => {
    const r = await run('cloudflare', () => client.domainViaCloudflare(cfToken));
    if (r) { setCfToken(''); setCfDone(r.data.steps || []); onChange(r.data.domain); }
  };
  const check = async () => { const r = await run('check', () => client.checkDomain()); if (r) onChange(r.data); };
  const disconnect = async () => {
    const ok = await showConfirm({ title: 'STOP USING THIS DOMAIN', message: `Email goes back to ${fallbackFrom}. You can connect it again later.`, confirmLabel: 'DISCONNECT', cancelLabel: 'KEEP IT', variant: 'danger' });
    if (!ok) return;
    const r = await run('disconnect', () => client.disconnectDomain());
    if (r) onChange(null);
  };
  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1500); } catch { /* clipboard blocked */ }
  };

  if (!domain) {
    if (readOnly) return null;
    return (
      <div className="flex flex-col" style={{ gap: 10 }} data-testid="own-domain">
        {!open ? (
          <button type="button" onClick={() => setOpen(true)} className="self-start font-mono font-extrabold underline" style={{ border: 'none', background: 'none', padding: 0, fontSize: 10, letterSpacing: '.14em', color: '#000' }}>
            USE YOUR OWN DOMAIN →
          </button>
        ) : (
          <div className="flex flex-col" style={{ gap: 10, padding: 18, borderRadius: 22, background: '#F5F5F5' }}>
            <span style={{ fontSize: 11, lineHeight: 1.6, color: '#4B5563' }}>
              Send from your own address, like <b>hello@yourbusiness.com</b>. You&rsquo;ll add three records where your domain&rsquo;s DNS lives; we switch over the moment they&rsquo;re live. Gmail, Outlook and other free addresses can&rsquo;t be used.
            </span>
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="hello@yourbusiness.com" aria-label="Send from address" type="email"
                onKeyDown={(e) => { if (e.key === 'Enter') connect(); }}
                className="min-w-0 flex-1 rounded-[14px] border-2 border-transparent bg-white font-mono text-[13px] outline-none focus:border-black" style={{ height: 46, padding: '0 16px', minWidth: 220 }} />
              <button type="button" disabled={!address.trim() || busy} onClick={connect} className={`${PILL} bg-[#0A0A0A] text-white`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em' }}>{busy === 'connect' ? 'CONNECTING…' : 'CONNECT'}</button>
              <button type="button" onClick={() => { setOpen(false); setError(null); }} className={`${PILL} bg-white`} style={{ height: 46, padding: '0 16px', fontSize: 11, letterSpacing: '.14em' }}>CANCEL</button>
            </div>
            {error && <span style={{ fontSize: 11, lineHeight: 1.6, color: '#B91C1C' }} role="alert">{error}</span>}
          </div>
        )}
      </div>
    );
  }

  const [label, bg, fg] = STATUS[domain.status] || STATUS.pending;
  const mailBody = `Hi, could you add these DNS records for ${domain.domain}? They let us send our email from ${domain.fromAddress}.\n\n`
    + (opts?.records || domain.records).map((r) => `${r.type}  ${r.host || r.name}${r.optional ? '  (only if there is no DMARC record already)' : ''}\n  ${r.value}`).join('\n\n')
    + '\n\nThanks!';
  return (
    <div className="flex flex-col" style={{ gap: 12, padding: 18, borderRadius: 22, background: '#F5F5F5' }} data-testid="own-domain">
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: '#6B7280' }}>YOUR DOMAIN</span>
        <b style={{ fontSize: 12 }}>{domain.fromAddress}</b>
        <span data-testid="domain-status" className="flex items-center" style={{ height: 22, padding: '0 10px', borderRadius: 999, background: bg, color: fg, fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>{label}</span>
      </div>
      {domain.status !== 'verified' && (
        <span style={{ fontSize: 11, lineHeight: 1.6, color: '#4B5563' }}>
          {domain.status === 'broken'
            ? <>Some of these records are gone from {domain.domain}&rsquo;s DNS, so email is going out from <b>{fallbackFrom}</b> again. Put them back and it switches over by itself.</>
            : <>Add these where {domain.domain}&rsquo;s DNS lives (GoDaddy, Namecheap, Cloudflare, Squarespace, Wix…). We check every few minutes and switch over by ourselves, usually within the hour. Until then email goes out from <b>{fallbackFrom}</b>.</>}
        </span>
      )}
      {opts && domain.status !== 'verified' && (
        <div className="flex flex-col" style={{ gap: 10 }} data-testid="domain-help">
          {opts.applyUrl && !readOnly && (
            <a href={opts.applyUrl} className={`${PILL} bg-[#0A0A0A] text-white no-underline`} style={{ height: 46, padding: '0 20px', fontSize: 11, letterSpacing: '.14em', alignSelf: 'flex-start' }} data-testid="domain-connect-apply">
              CONNECT WITH {(opts.provider || 'YOUR DNS HOST').toUpperCase()} →
            </a>
          )}
          <span style={{ fontSize: 11, lineHeight: 1.6, color: '#4B5563' }}>
            {opts.provider
              ? <>Your DNS is at <b>{opts.provider}</b>.{opts.applyUrl ? ' Approve it there in one click, or add the records yourself:' : ' Add these records there:'}</>
              : <>Add these where {opts.zone}&rsquo;s DNS lives:</>}
            {opts.providerLink && <> <a href={opts.providerLink} target="_blank" rel="noopener noreferrer" className="font-bold underline">OPEN {opts.provider.toUpperCase()} DNS →</a></>}
          </span>
          {opts.cloudflare && !readOnly && (
            <div className="flex flex-col bg-white" style={{ gap: 8, padding: 14, borderRadius: 16 }} data-testid="domain-cloudflare">
              <span style={{ fontSize: 11, lineHeight: 1.6, color: '#4B5563' }}>
                Or let us add them: in Cloudflare open <b>My Profile → API Tokens → Create Token → Edit zone DNS</b>, pick <b>{opts.zone}</b>, create it and paste it here. We use it once and don&rsquo;t keep it.
              </span>
              <div className="flex flex-wrap" style={{ gap: 8 }}>
                <input value={cfToken} onChange={(e) => setCfToken(e.target.value)} placeholder="Cloudflare API token" aria-label="Cloudflare API token" type="password" autoComplete="off"
                  className="min-w-0 flex-1 rounded-[14px] border-2 border-transparent bg-[#F5F5F5] font-mono text-[13px] outline-none focus:border-black" style={{ height: 42, padding: '0 14px', minWidth: 200 }} />
                <button type="button" disabled={!cfToken.trim() || !!busy} onClick={viaCloudflare} className={`${PILL} bg-[#0A0A0A] text-white`} style={{ height: 42, padding: '0 18px', fontSize: 10, letterSpacing: '.14em' }}>{busy === 'cloudflare' ? 'ADDING…' : 'ADD THEM FOR ME'}</button>
              </div>
              {cfDone && <span style={{ fontSize: 10, lineHeight: 1.6, color: '#15803D' }}>{cfDone.join(' · ')}</span>}
            </div>
          )}
        </div>
      )}
      <div className="flex flex-col" style={{ gap: 6 }} data-testid="domain-records">
        {(opts?.records || domain.records).map((r) => (
          <div key={`${r.type}-${r.name}`} className="grid items-center bg-white" style={{ gridTemplateColumns: '30px 56px minmax(0,1fr)', gap: '6px 10px', padding: '10px 12px', borderRadius: 16 }}>
            <span aria-label={r.ok ? 'Found' : 'Not found yet'} className="flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: '50%', background: r.ok ? '#22C55E' : '#E5E7EB', color: '#fff', fontSize: 11 }}>{r.ok ? '✓' : ''}</span>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.12em' }}>{r.type}{r.optional ? <span style={{ display: 'block', fontSize: 8, color: '#9CA3AF', fontWeight: 700 }}>IF NONE</span> : null}</span>
            <div className="flex min-w-0 flex-col" style={{ gap: 4 }}>
              <CopyLine label="NAME" text={r.host || r.name} title={r.name} copied={copied === `${r.name}-n`} onCopy={() => copy(r.host || r.name, `${r.name}-n`)} />
              <CopyLine label="VALUE" text={r.value} copied={copied === `${r.name}-v`} onCopy={() => copy(r.value, `${r.name}-v`)} />
            </div>
          </div>
        ))}
      </div>
      {error && <span style={{ fontSize: 11, color: '#B91C1C' }} role="alert">{error}</span>}
      {!readOnly && (
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {domain.status !== 'verified' && <button type="button" disabled={!!busy} onClick={check} className={`${PILL} bg-[#0A0A0A] text-white`} style={{ height: 40, padding: '0 18px', fontSize: 10, letterSpacing: '.14em' }}>{busy === 'check' ? 'CHECKING…' : 'CHECK NOW'}</button>}
          <a href={`mailto:?subject=${encodeURIComponent(`DNS records for ${domain.domain}`)}&body=${encodeURIComponent(mailBody)}`} className={`${PILL} bg-white text-black no-underline`} style={{ height: 40, padding: '0 18px', fontSize: 10, letterSpacing: '.14em' }}>EMAIL THESE TO SOMEONE</a>
          <button type="button" disabled={!!busy} onClick={disconnect} className={`${PILL}`} style={{ height: 40, padding: '0 14px', fontSize: 10, letterSpacing: '.12em', background: 'transparent', color: '#EF4444', border: 'none' }}>DISCONNECT</button>
        </div>
      )}
    </div>
  );
}

function CopyLine({ label, text, title, copied, onCopy }) {
  return (
    <div className="flex min-w-0 items-center" style={{ gap: 8 }}>
      <span className="flex-none" style={{ width: 40, fontSize: 9, letterSpacing: '.14em', color: '#9CA3AF' }}>{label}</span>
      <code className="min-w-0 flex-1 truncate" style={{ fontSize: 11 }} title={title || text}>{text}</code>
      <button type="button" onClick={onCopy} aria-label={`Copy ${label.toLowerCase()}`} className="flex-none rounded-full bg-[#F2F2F2] font-mono font-extrabold hover:bg-[#E5E5E5]" style={{ height: 26, padding: '0 10px', border: 'none', fontSize: 9, letterSpacing: '.12em' }}>{copied ? 'COPIED' : 'COPY'}</button>
    </div>
  );
}
